import React, { useState, useEffect } from 'react';
import { serviceAPI, machinesAPI } from '../api/api';
import DataTable from '../components/DataTable';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Plus, X, Save, Wrench, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PasswordModal from '../components/PasswordModal';
import { verifyAdminPassword } from '../utils/passwordUtils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ServicePage = () => {
  const [serviceLogs, setServiceLogs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Parts for selected machine
  const [availableParts, setAvailableParts] = useState([]);
  const [selectedPartsList, setSelectedPartsList] = useState([]); // [{part, quantity}]
  const [currentPartId, setCurrentPartId] = useState('');
  const [currentQuantity, setCurrentQuantity] = useState(1);
  
  const [formData, setFormData] = useState({
    machine_id: '',
    service_date: new Date().toISOString().split('T')[0],
    service_type: '',
    description: '',
    technician: '',
    counters_at_service: '',
    labor_cost: 0,
    parts_used: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [serviceRes, machinesRes] = await Promise.all([
        serviceAPI.getAll(),
        machinesAPI.getAll()
      ]);
      setServiceLogs(serviceRes.data);
      setMachines(machinesRes.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  const fetchMachineParts = async (machineId) => {
    if (!machineId) {
      setAvailableParts([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/consumables/machine/${machineId}/parts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableParts(response.data);
      setSelectedPartsList([]);
    } catch (error) {
      console.error('Error fetching parts:', error);
      setAvailableParts([]);
    }
  };

  const handleMachineChange = (machineId) => {
    setFormData({...formData, machine_id: machineId});
    fetchMachineParts(machineId);
    setSelectedPartsList([]);
    setCurrentPartId('');
  };

  const handleAddPart = () => {
    if (!currentPartId) return;
    
    const part = availableParts.find(p => p.id === parseInt(currentPartId));
    if (!part) return;
    
    const quantity = parseInt(currentQuantity) || 1;
    
    // Add to list
    setSelectedPartsList(prev => [...prev, { part, quantity }]);
    
    // Reset selection (keep dropdown open for next selection)
    setCurrentPartId('');
    setCurrentQuantity(1);
    
    toast.success(`${part.name} προστέθηκε (x${quantity})`);
  };

  const handleRemovePart = (index) => {
    setSelectedPartsList(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotalPartsCost = () => {
    return selectedPartsList.reduce((sum, item) => {
      return sum + (parseFloat(item.part.cost_price || 0) * item.quantity);
    }, 0);
  };

  const calculatePartsString = () => {
    if (selectedPartsList.length === 0) return '';
    
    // Group by part name and sum quantities
    const grouped = selectedPartsList.reduce((acc, item) => {
      const key = item.part.name;
      if (!acc[key]) {
        acc[key] = { part: item.part, totalQty: 0 };
      }
      acc[key].totalQty += item.quantity;
      return acc;
    }, {});
    
    // Build parts string with individual entries
    const entries = [];
    Object.values(grouped).forEach(({ part, totalQty }) => {
      const unitCost = parseFloat(part.cost_price || 0);
      const totalCost = unitCost * totalQty;
      
      // Add each unit separately (as per original format)
      for (let i = 0; i < totalQty; i++) {
        entries.push(`${part.name} [${unitCost.toFixed(2)}€]`);
      }
    });
    
    return entries.join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const partsString = calculatePartsString();
    const submitData = {
      ...formData,
      parts_used: partsString,
      labor_cost: parseFloat(formData.labor_cost) || 0
    };
    
    try {
      if (editingService) {
        await serviceAPI.update(editingService.id, submitData);
        toast.success('Η καταχώρηση ενημερώθηκε!');
      } else {
        await serviceAPI.create(submitData);
        toast.success('Η καταχώρηση δημιουργήθηκε!');
      }
      fetchData();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    }
  };

  const handleEdit = (service) => {
    setPendingAction({ type: 'edit', service });
    setShowPasswordModal(true);
  };

  const handleDelete = (log) => {
    setPendingAction({ type: 'delete', log });
    setShowPasswordModal(true);
  };

  const handlePasswordConfirm = async (password) => {
    const isValid = await verifyAdminPassword(password);
    
    if (isValid && pendingAction) {
      setShowPasswordModal(false);
      
      if (pendingAction.type === 'edit') {
        setEditingService(pendingAction.service);
        setFormData({
          ...pendingAction.service,
          labor_cost: pendingAction.service.labor_cost || 0
        });
        fetchMachineParts(pendingAction.service.machine_id);
        
        // Parse existing parts_used to build selectedPartsList
        // Format: "Part1 [10.00€], Part2 [20.00€], Part1 [10.00€]"
        const partsStr = pendingAction.service.parts_used || '';
        if (partsStr) {
          setTimeout(() => {
            const partsList = [];
            // Split by comma and parse each entry
            const entries = partsStr.split(',').map(p => p.trim());
            
            entries.forEach(entry => {
              // Extract name and price
              const match = entry.match(/^(.+?)\s*\[(\d+\.?\d*)€\]$/);
              if (match) {
                const partName = match[1].trim();
                const price = parseFloat(match[2]);
                
                // Find matching part in available parts
                const matchingPart = availableParts.find(p => 
                  p.name.toLowerCase() === partName.toLowerCase()
                );
                
                if (matchingPart) {
                  partsList.push({ part: matchingPart, quantity: 1 });
                }
              }
            });
            
            setSelectedPartsList(partsList);
          }, 500);
        }
        
        setShowModal(true);
      } else if (pendingAction.type === 'delete') {
        try {
          await serviceAPI.delete(pendingAction.log.id);
          toast.success('Η καταχώρηση διαγράφηκε!');
          fetchData();
        } catch (error) {
          toast.error('Σφάλμα διαγραφής');
        }
      }
      
      setPendingAction(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
    setAvailableParts([]);
    setSelectedPartsList([]);
    setCurrentPartId('');
    setCurrentQuantity(1);
    setFormData({
      machine_id: '',
      service_date: new Date().toISOString().split('T')[0],
      service_type: '',
      description: '',
      technician: '',
      counters_at_service: '',
      labor_cost: 0,
      parts_used: ''
    });
  };

  const enrichedLogs = serviceLogs.map(log => {
    const machine = machines.find(m => m.id === log.machine_id);
    return {
      ...log,
      machine_info: machine ? `${machine.brand} ${machine.model}` : 'N/A',
      machine_serial: machine?.machine_serial || '',
      customer_name: machine?.company_name || 'N/A'
    };
  });

  const columns = [
    { 
      key: 'service_date', 
      label: 'Ημ/νία',
      render: (item) => new Date(item.service_date).toLocaleDateString('el-GR')
    },
    { key: 'customer_name', label: 'Πελάτης' },
    { key: 'machine_serial', label: 'S/N' },
    { key: 'machine_info', label: 'Μηχάνημα' },
    { key: 'service_type', label: 'Τύπος' },
    { key: 'technician', label: 'Τεχνικός' },
    { 
      key: 'description', 
      label: 'Περιγραφή',
      render: (item) => item.description?.substring(0, 50) + (item.description?.length > 50 ? '...' : '')
    },
  ];

  if (loading) {
    return <div className="flex justify-center py-12">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Service</h1>
          <p className="text-gray-600">Καταχωρήσεις service</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#004a99] hover:bg-[#003a7a]">
          <Plus className="h-4 w-4 mr-2" />
          Νέα Καταχώρηση
        </Button>
      </div>

      <DataTable
        data={enrichedLogs}
        columns={columns}
        groupByKey="machine_serial"
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchPlaceholder="Αναζήτηση service..."
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-300">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingService ? 'Επεξεργασία Service' : 'Νέα Καταχώρηση'}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Machine Selection */}
                <div>
                  <label className="text-sm font-medium">Μηχάνημα *</label>
                  <select
                    value={formData.machine_id}
                    onChange={(e) => handleMachineChange(Number(e.target.value))}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Επιλέξτε μηχάνημα</option>
                    {machines.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.company_name} - {m.brand} {m.model} ({m.machine_serial})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date, Type, Technician */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">Ημερομηνία *</label>
                    <Input
                      type="date"
                      value={formData.service_date}
                      onChange={(e) => setFormData({...formData, service_date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Τύπος Service</label>
                    <Input
                      value={formData.service_type || ''}
                      onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                      placeholder="π.χ. Προληπτικό, Επισκευή"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Τεχνικός</label>
                    <Input
                      value={formData.technician}
                      onChange={(e) => setFormData({...formData, technician: e.target.value})}
                      placeholder="Όνομα τεχνικού"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm font-medium">Περιγραφή</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows="3"
                    placeholder="Περιγραφή εργασίας..."
                  />
                </div>

                {/* Counters and Labor Cost */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Μετρητές κατά Service</label>
                    <Input
                      value={formData.counters_at_service}
                      onChange={(e) => setFormData({...formData, counters_at_service: e.target.value})}
                      placeholder="π.χ. Black: 15000, Color: 3500"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">💰 Κόστος Εργασίας (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.labor_cost}
                      onChange={(e) => setFormData({...formData, labor_cost: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Parts Section */}
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Wrench className="h-5 w-5 text-blue-600" />
                    <label className="text-sm font-semibold text-blue-900">Ανταλλακτικά που χρησιμοποιήθηκαν</label>
                  </div>
                  
                  {!formData.machine_id ? (
                    <p className="text-sm text-gray-500 italic">Επιλέξτε πρώτα μηχάνημα...</p>
                  ) : availableParts.length === 0 ? (
                    <p className="text-sm text-blue-600">ℹ️ Δεν βρέθηκαν ανταλλακτικά για αυτό το μοντέλο.</p>
                  ) : (
                    <>
                      {/* Add Part Form */}
                      <div className="bg-white p-3 rounded border border-gray-300 space-y-3">
                        <div className="grid grid-cols-12 gap-3">
                          <div className="col-span-7">
                            <label className="text-xs font-medium text-gray-700">Επιλέξτε Ανταλλακτικό</label>
                            <select
                              value={currentPartId}
                              onChange={(e) => setCurrentPartId(e.target.value)}
                              className="w-full h-9 px-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">-- Επιλέξτε --</option>
                              {availableParts.map(part => (
                                <option key={part.id} value={part.id}>
                                  {part.name} ({parseFloat(part.cost_price || 0).toFixed(2)}€)
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-3">
                            <label className="text-xs font-medium text-gray-700">Ποσότητα</label>
                            <Input
                              type="number"
                              min="1"
                              value={currentQuantity}
                              onChange={(e) => setCurrentQuantity(e.target.value)}
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="col-span-2 flex items-end">
                            <Button
                              type="button"
                              onClick={handleAddPart}
                              disabled={!currentPartId}
                              className="w-full h-9 bg-green-600 hover:bg-green-700 text-sm"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Selected Parts List */}
                      {selectedPartsList.length > 0 && (
                        <div className="mt-3 bg-white rounded border border-gray-300">
                          <div className="bg-gray-50 px-3 py-2 border-b">
                            <p className="text-sm font-semibold text-gray-700">
                              📋 Επιλεγμένα Ανταλλακτικά ({selectedPartsList.length})
                            </p>
                          </div>
                          <div className="divide-y max-h-48 overflow-y-auto">
                            {selectedPartsList.map((item, index) => (
                              <div key={index} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                                <div className="flex-1">
                                  <span className="text-sm font-medium">{item.part.name}</span>
                                  <span className="text-xs text-gray-600 ml-2">
                                    x{item.quantity} × {parseFloat(item.part.cost_price || 0).toFixed(2)}€ = 
                                    <span className="font-semibold text-orange-600 ml-1">
                                      {(parseFloat(item.part.cost_price || 0) * item.quantity).toFixed(2)}€
                                    </span>
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemovePart(index)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                          <div className="bg-green-50 px-3 py-2 border-t border-green-200">
                            <p className="text-sm font-bold text-green-700">
                              💰 Σύνολο Ανταλλακτικών: {calculateTotalPartsCost().toFixed(2)}€
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseModal}>
                    Άκυρο
                  </Button>
                  <Button type="submit" className="bg-[#004a99] hover:bg-[#003a7a]">
                    <Save className="h-4 w-4 mr-2" />
                    Αποθήκευση
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={handlePasswordConfirm}
        title={pendingAction?.type === 'delete' ? 'Επιβεβαίωση Διαγραφής' : 'Επιβεβαίωση Επεξεργασίας'}
      />
    </div>
  );
};

export default ServicePage;
